(() => {
  if (customElements.get('ft-workwear-bundle')) return;
  class WorkwearBundle extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      if (!this.form || this.controller) return;
      this.section = document.getElementById(`ProductSection-${this.dataset.sectionId}`) || this.form.parentElement;
      this.form.enctype = 'multipart/form-data';
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('change', event => {
        if (event.target.matches('[data-bundle-extra]')) this.applyExtras();
      }, options);
      this.section.addEventListener('change', event => {
        if (event.target.closest(`variant-selects .pvOpt${Number(this.dataset.packIndex)}, variant-radios .pvOpt${Number(this.dataset.packIndex)}`)) this.syncExtras();
      }, { ...options, capture:true });
      const preload = event => {
        const swatch = event.target.closest('[data-bundle-colour]');
        if (swatch) this.preloadPhoto(swatch.dataset.colourImage);
      };
      this.addEventListener('pointerover', preload, options);
      this.addEventListener('focusin', preload, options);
      this.addEventListener('click', event => {
        const photo = event.target.closest('[data-bundle-photo]');
        const dialog = this.querySelector('[data-bundle-photo-dialog]');
        if (photo && !photo.hidden && dialog) {
          const image = photo.querySelector('img');
          const large = dialog.querySelector('[data-bundle-large-photo]');
          large.src = image.currentSrc || image.src; large.alt = image.alt;
          dialog.showModal();
        }
        if (event.target.closest('[data-bundle-close-photo]')) dialog?.close();
        if (event.target === dialog) dialog.close();
      }, options);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-bundle-colour]');
        if (!button || button.disabled) return;
        this.setColour(button.closest('[data-bundle-item]'), button.dataset.bundleColour);
        this.update();
      }, options);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-bundle-colour-select]')) return;
        this.setColour(event.target.closest('[data-bundle-item]'), event.target.value);
        this.update();
      }, options);
      this.logoInput = this.querySelector('input[type="file"]');
      this.logoInput?.addEventListener('change', () => this.readLogo(), options);
      this.querySelector('[data-bundle-remove-logo]')?.addEventListener('click', event => { event.preventDefault(); this.removeSavedLogo(); }, options);
      this.section.addEventListener('change', () => this.update(), options);
      this.addEventListener('input', event => { if (!event.target.matches('[data-bundle-extra]')) this.update(); }, options);
      this.form.addEventListener('submit', event => {
        this.update();
        // Bypass the theme's AJAX serializer so Shopify receives the file itself.
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.valid && this.form.checkValidity() && !this.form.querySelector('[name="id"]')?.disabled) {
          HTMLFormElement.prototype.submit.call(this.form);
        } else { this.focusInvalidCard(); this.form.reportValidity(); }
      }, { ...options, capture: true });
      document.addEventListener('click', event => {
        const button = event.target.closest('button, input[type="submit"], .shopify-payment-button');
        if (!button || !(button.form === this.form || this.form.contains(button)) ||
            !(button.matches('[name="add"], [type="submit"], .shopify-payment-button') || button.closest('.shopify-payment-button'))) return;
        this.update();
        if (!this.valid) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.focusInvalidCard();
        }
      }, { ...options, capture: true });
      this.observer = new MutationObserver(() => { this.update(); this.paintLogo(); });
      this.observer.observe(this.section, { childList: true, subtree: true });
      this.restoreOrder();
      this.update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
      this.clearPreview();
      this.extrasPicker?.classList.remove('ft-bundle-native-extras');
      this.extrasPicker?.removeAttribute('aria-hidden');
      this.querySelector('[data-bundle-photo-dialog]')?.close();
    }
    extraRows() { return [...this.querySelectorAll('[data-bundle-extra]')]; }
    extraItem(input) { return this.querySelector(`[data-bundle-item][data-item-index="${input.dataset.bundleExtra}"]`); }
    extraMembers(value) {
      return this.extraRows().filter(input => (JSON.parse(this.extraItem(input)?.dataset.extras || 'null') || []).includes(value));
    }
    packVariants() {
      const extrasIndex = Number(this.dataset.extrasIndex);
      let variants;
      try { variants = JSON.parse(this.querySelector('[data-bundle-variants]')?.textContent || 'null'); } catch { return []; }
      return Array.isArray(variants) ? variants.filter(variant => Array.isArray(variant.options) && variant.options.every((value,index) => index === extrasIndex || value === this.optionValue(index, index === Number(this.dataset.packIndex) ? this.dataset.pack : undefined))) : [];
    }
    extraVariant(variants, checked) {
      const index = Number(this.dataset.extrasIndex);
      return variants.find(variant => variant.available && (checked.length ? this.extraMembers(variant.options[index]).length === checked.length && checked.every(input => this.extraMembers(variant.options[index]).includes(input)) : variant.options[index] === 'No extras'));
    }
    chooseExtraVariant(variant) {
      if (!variant) return;
      const index = Number(this.dataset.extrasIndex);
      const value = variant.options[index];
      if (this.optionValue(index, this.dataset.extras) !== value) {
        const input = this.selectOption(index, value);
        input?.dispatchEvent(new Event('change', {bubbles:true}));
      }
      this.lastExtras = value;
      this.extraRows().forEach(input => { input.checked = this.extraMembers(value).includes(input); });
    }
    applyExtras() {
      if (this.syncingExtras) return;
      const variant = this.extraVariant(this.packVariants(), this.extraRows().filter(input => input.checked && !input.disabled));
      const message = this.querySelector('[data-bundle-extras-message]');
      if (variant) {
        this.chooseExtraVariant(variant);
        if (message) message.hidden = true;
      } else {
        const selected = this.optionValue(Number(this.dataset.extrasIndex), this.dataset.extras);
        this.extraRows().forEach(input => { input.checked = !input.disabled && this.extraMembers(selected).includes(input); });
        if (message) { this.setText(message, 'This combination of extras is not available for this pack size.'); message.hidden = false; }
      }
      this.update();
    }
    syncExtras() {
      const box = this.querySelector('[data-bundle-extras]');
      const variants = this.packVariants();
      const index = Number(this.dataset.extrasIndex);
      if (!box || index < 0 || !variants.length || this.syncingExtras) return;
      this.syncingExtras = true;
      try {
        this.extrasPicker = this.section.querySelector(`variant-selects .pvOpt${index}, variant-radios .pvOpt${index}`);
        this.extrasPicker?.classList.add('ft-bundle-native-extras');
        this.extrasPicker?.setAttribute('aria-hidden', 'true');
        this.extrasPicker?.querySelectorAll('input,select,a,button').forEach(input => { input.tabIndex = -1; });
        box.hidden = false;
        const pack = this.optionValue(Number(this.dataset.packIndex), this.dataset.pack);
        const selected = this.optionValue(index, this.dataset.extras);
        const changedPack = this.lastPack !== undefined && this.lastPack !== pack;
        if (!changedPack && selected !== this.lastExtras) this.extraRows().forEach(input => { input.checked = this.extraMembers(selected).includes(input); });
        const base = variants.find(variant => variant.options[index] === 'No extras');
        for (const input of this.extraRows()) {
          const item = this.extraItem(input);
          const row = input.closest('label');
          const variant = this.extraVariant(variants, [input]);
          const quantity = Number(JSON.parse(item.dataset.quantities)[pack]);
          input.disabled = !variant || !base || !Number.isSafeInteger(quantity) || quantity <= 0;
          if (input.disabled) input.checked = false;
          this.setText(row.querySelector('[data-bundle-extra-title]'), `${Number.isFinite(quantity) ? quantity : '?'} × ${item.dataset.name}`);
          let price = 'Not available for this pack size';
          if (!input.disabled) {
            try { price = `+ ${new Intl.NumberFormat(box.dataset.locale, {style:'currency',currency:box.dataset.currency}).format((variant.price - base.price) / 100)}`; }
            catch { price = `+ ${((variant.price - base.price) / 100).toFixed(2)}`; }
          }
          this.setText(row.querySelector('[data-bundle-extra-price]'), price);
        }
        const checked = this.extraRows().filter(input => input.checked && !input.disabled);
        let target = this.extraVariant(variants, checked);
        if (!target) {
          // Keep the largest supported subset without adding unrequested items.
          target = variants.filter(variant => variant.available && this.extraMembers(variant.options[index]).every(input => checked.includes(input)))
            .sort((a,b) => this.extraMembers(b.options[index]).length - this.extraMembers(a.options[index]).length)[0];
        }
        this.lastPack = pack;
        this.chooseExtraVariant(target);
        this.lastExtras = this.optionValue(index, this.dataset.extras);
      } finally { this.syncingExtras = false; }
    }
    photoURL(value) {
      try { const url = new URL(value); return ['https:','http:'].includes(url.protocol) ? url.href : null; } catch { return null; }
    }
    preloadPhoto(value) {
      const url = this.photoURL(value);
      if (!url) return;
      this.preloadedPhotos ||= new Map();
      if (!this.preloadedPhotos.has(url)) { const image = new Image(); image.src = url; this.preloadedPhotos.set(url,image); }
    }
    syncPhoto(item, colour) {
      const photo = item.querySelector('[data-bundle-photo]');
      const image = item.querySelector('[data-bundle-colour-image]');
      if (!photo || !image) return;
      const fallback = this.photoURL(item.dataset.image);
      const primary = this.photoURL(colour?.image) || fallback;
      const signature = JSON.stringify([primary,fallback]);
      if (item.dataset.photoSignature === signature) return;
      item.dataset.photoSignature = signature;
      image.alt = `${item.dataset.name}${colour ? ` – ${colour.name}` : ''}`;
      image.hidden = !primary;
      photo.hidden = !primary;
      if (!primary) { image.removeAttribute('src'); return; }
      image.style.opacity = '.35';
      image.onload = () => { if (item.dataset.photoSignature === signature) image.style.opacity = '1'; };
      image.onerror = () => {
        if (item.dataset.photoSignature !== signature) return;
        if (fallback && image.getAttribute('src') !== fallback) image.src = fallback;
        else { photo.hidden = true; image.hidden = true; image.removeAttribute('src'); }
      };
      image.src = primary;
    }
    clearPreview() {
      if (this.logoObjectURL) URL.revokeObjectURL(this.logoObjectURL);
      this.logoObjectURL = null;
      this.section?.querySelectorAll('[data-bundle-logo-preview]').forEach(image => image.remove());
    }
    readLogo() {
      this.clearPreview();
      const file = this.logoInput?.files[0];
      const status = this.querySelector('[data-bundle-logo-status]');
      const pdf = file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name));
      if (status) {
        this.setText(status, pdf ? 'Logo added (PDF)' : '');
        status.hidden = !pdf;
      }
      if (file?.type.startsWith('image/') && !pdf) {
        this.logoObjectURL = URL.createObjectURL(file);
        this.paintLogo();
        if (this.section.querySelector('[data-bundle-logo-preview]')) {
          const kit = this.section.querySelector('[data-bundle-kit]');
          const slider = kit?.closest('.primgSlider');
          const index = Number(kit?.closest('[data-slide]')?.dataset.slide || 0);
          if (slider?.swiper?.slideToLoop) slider.swiper.slideToLoop(index);
          else if (slider?.swiper?.slideTo) slider.swiper.slideTo(index);
          else kit?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      }
      this.update();
    }
    paintLogo() {
      if (!this.logoObjectURL) return;
      let area;
      try { area = JSON.parse(this.dataset.logoArea || 'null'); } catch { return; }
      if (!area || !['x','y','w','h'].every(key => typeof area[key] === 'number' && Number.isFinite(area[key])) ||
          area.x < 0 || area.y < 0 || area.w <= 0 || area.h <= 0 || area.x + area.w > 1 || area.y + area.h > 1) return;
      for (const wrapper of this.section.querySelectorAll('[data-bundle-kit]')) {
        let image = wrapper.querySelector('[data-bundle-logo-preview]');
        if (!image) {
          image = document.createElement('img');
          image.dataset.bundleLogoPreview = '';
          image.className = 'ft-bundle-logo-preview';
          image.alt = 'Your logo preview';
          wrapper.append(image);
        }
        if (image.getAttribute('src') !== this.logoObjectURL) image.src = this.logoObjectURL;
        Object.assign(image.style, {left:`${area.x * 100}%`,top:`${area.y * 100}%`,width:`${area.w * 100}%`,height:`${area.h * 100}%`});
      }
    }
    selectOption(index, value) {
      if (index < 0 || typeof value !== 'string') return null;
      const group = this.section.querySelector(`variant-selects .pvOpt${index}, variant-radios .pvOpt${index}`);
      const select = group?.querySelector('select');
      if (select && [...select.options].some(option => option.value === value && !option.disabled)) {
        select.value = value;
        return select;
      }
      const radio = [...(group?.querySelectorAll('input[type="radio"]') || [])].find(input => input.value === value && !input.disabled);
      if (radio) radio.checked = true;
      return radio || null;
    }
    restoreOrder() {
      if (this.orderRestored) return;
      this.orderRestored = true;
      const code = new URLSearchParams(location.search).get('reorder');
      if (!code || code.length > 32768 || !/^[A-Za-z0-9_-]+$/.test(code)) return;
      let order;
      try {
        const base64 = code.replace(/-/g, '+').replace(/_/g, '/');
        const bytes = Uint8Array.from(atob(base64 + '='.repeat((4 - base64.length % 4) % 4)), char => char.charCodeAt(0));
        order = JSON.parse(new TextDecoder('utf-8', {fatal:true}).decode(bytes));
      } catch { return; }
      if (!order || typeof order !== 'object' || Array.isArray(order)) return;
      const packIndex = Number(this.dataset.packIndex);
      const extrasIndex = Number(this.dataset.extrasIndex);
      const previousPack = this.optionValue(packIndex, this.dataset.pack);
      const previousExtras = this.optionValue(extrasIndex, this.dataset.extras);
      let changes = [this.selectOption(packIndex, order.pack), this.selectOption(extrasIndex, order.extras)].filter(Boolean);
      const variantsJSON = this.section.querySelector('variant-selects script[type="application/json"], variant-radios script[type="application/json"]');
      if (variantsJSON) {
        try {
          const variants = JSON.parse(variantsJSON.textContent);
          if (Array.isArray(variants) && !variants.some(variant => variant.options.every((value, index) => value === this.optionValue(index)))) {
            this.selectOption(packIndex, previousPack);
            this.selectOption(extrasIndex, previousExtras);
            changes = [];
          }
        } catch { /* Retain normal theme selection handling when variant data is absent. */ }
      }
      // Set both options before notifying the theme so it resolves the final combination.
      changes.forEach(input => input.dispatchEvent(new Event('change', {bubbles:true})));
      const picker = this.section.querySelector('variant-selects, variant-radios');
      // Bundle scripts can upgrade before the deferred native picker script.
      // Replay once it is ready so reorder selects the priced variant, not just its fields.
      if (changes.length && picker && !customElements.get(picker.localName)) {
        customElements.whenDefined(picker.localName).then(() => {
          if (!this.isConnected || !this.controller) return;
          changes.filter(input => input.isConnected).forEach(input => input.dispatchEvent(new Event('change', {bubbles:true})));
          this.update();
        });
      }
      for (const item of this.querySelectorAll('[data-bundle-item]')) {
        const colourProperty = item.querySelector('[data-bundle-colour-property]');
        const colourName = colourProperty?.name.slice(11, -1);
        if (order.colours && Object.hasOwn(order.colours, colourName)) this.setColour(item, order.colours[colourName]);
        this.syncColour(item);
        const property = item.querySelector('[data-bundle-property]');
        const name = property?.name.slice(11, -1);
        if (!order.sizes || !Object.hasOwn(order.sizes, name) || typeof order.sizes[name] !== 'string') continue;
        for (const pair of order.sizes[name].split(',')) {
          const split = pair.lastIndexOf(':');
          if (split < 0) continue;
          const size = pair.slice(0, split).trim();
          const amount = pair.slice(split + 1).trim();
          if (!/^\d+$/.test(amount) || !Number.isSafeInteger(Number(amount))) continue;
          const input = [...item.querySelectorAll('[data-bundle-size]')].find(input => input.dataset.bundleSize === size);
          if (input) input.value = String(Number(amount));
        }
      }
      if (typeof order.logo === 'string' && this.logoInput) {
        let url;
        try { url = new URL(order.logo); } catch { return; }
        if (!['https:', 'http:'].includes(url.protocol)) return;
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = this.logoInput.name;
        hidden.value = url.href;
        hidden.dataset.bundleSavedLogo = '';
        this.append(hidden);
        this.logoInput.disabled = true;
        this.logoInput.hidden = true;
        this.querySelector('[data-bundle-reused-logo]').hidden = false;
      }
    }
    removeSavedLogo() {
      this.querySelector('[data-bundle-saved-logo]')?.remove();
      this.logoInput.disabled = false;
      this.logoInput.hidden = false;
      this.querySelector('[data-bundle-reused-logo]').hidden = true;
      this.update();
    }
    optionValue(index, fallback) {
      if (index < 0) return fallback;
      const group = this.section.querySelector(`variant-selects .pvOpt${index}, variant-radios .pvOpt${index}`);
      return group?.querySelector('select')?.value ?? group?.querySelector('input:checked')?.value ?? fallback;
    }
    setText(node, value) {
      if (node.textContent !== value) node.textContent = value;
    }
    focusInvalidCard() {
      const card = [...this.querySelectorAll('[data-bundle-item]')].find(item => item.dataset.visible === 'true' && item.dataset.incomplete === 'true');
      if (!card) return;
      this.querySelectorAll('[data-bundle-item][data-error]').forEach(item => item.removeAttribute('data-error'));
      card.dataset.error = 'true';
      card.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',block:'center'});
      const input = card.querySelector('[data-bundle-size]:enabled');
      input?.focus({preventScroll:true});
    }
    showItem(item, visible) {
      const previous = item.dataset.visible;
      const panel = item.closest('[data-bundle-extra-panel]');
      const entry = item.closest('[data-bundle-extra-entry]');
      const height = panel?.getBoundingClientRect().height || 0;
      const changed = previous !== String(visible);
      if (entry) entry.dataset.included = String(visible);
      item.dataset.visible = String(visible);
      if (!visible && previous === 'true') {
        item.querySelectorAll('[data-bundle-size]').forEach(input => { input.value = '0'; });
        const colours = this.colours(item);
        if (colours.length) this.setColour(item, item.dataset.defaultColour || item.dataset.initialColour || colours[0].name);
      }
      item.hidden = !visible;
      if (!panel || !changed) return;
      panel.inert = !visible;
      panel.setAttribute('aria-hidden',String(!visible));
      panel.getAnimations().forEach(animation => animation.cancel());
      if (previous === undefined || matchMedia('(prefers-reduced-motion: reduce)').matches) { panel.hidden = !visible; return; }
      panel.hidden = false;
      panel.dataset.closing = String(!visible);
      const expanded = visible ? panel.scrollHeight : height;
      const animation = panel.animate([{height:`${visible ? 0 : expanded}px`,opacity:visible?0:1},{height:`${visible ? expanded : 0}px`,opacity:visible?1:0}],{duration:200,easing:'ease-out'});
      animation.finished.then(() => { if (item.dataset.visible === 'false') { panel.hidden = true; panel.dataset.closing = 'false'; } }).catch(() => {});
    }
    colours(item) {
      try {
        const colours = JSON.parse(item.dataset.colours || 'null');
        return Array.isArray(colours) ? colours.filter(colour => typeof colour?.name === 'string' && Array.isArray(colour.sizes)) : [];
      } catch { return []; }
    }
    setColour(item, name) {
      if (!item || !this.colours(item).some(colour => colour.name === name)) return;
      item.dataset.colour = name;
      this.syncColour(item);
    }
    syncColour(item) {
      const colours = this.colours(item);
      if (!colours.length) { this.syncPhoto(item, null); return; }
      const colour = colours.find(colour => colour.name === item.dataset.colour) || colours[0];
      item.dataset.colour = colour.name;
      const property = item.querySelector('[data-bundle-colour-property]');
      if (property) property.value = colour.name;
      item.querySelectorAll('[data-bundle-colour]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.bundleColour === colour.name)));
      const select = item.querySelector('[data-bundle-colour-select]');
      if (select) select.value = colour.name;
      const chosen = item.querySelector('[data-bundle-chosen-colour]');
      if (chosen) this.setText(chosen, colour.name);
      this.syncPhoto(item, colour);
      const sizes = colour.sizes.map(String);
      const signature = JSON.stringify(sizes);
      if (item.dataset.colourSizes === signature) return;
      const row = item.querySelector('.ft-bundle-sizes');
      if (!row) return;
      const previous = new Map([...row.querySelectorAll('[data-bundle-size]')].map(input => [input.dataset.bundleSize, input.value]));
      const labels = sizes.map((size, index) => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'number';
        input.className = 'pr_input';
        input.min = '0'; input.step = '1'; input.inputMode = 'numeric';
        input.id = `BundleSize-${this.dataset.sectionId}-${item.dataset.itemIndex}-${index + 1}`;
        input.dataset.bundleSize = size;
        input.value = previous.get(size) ?? '0';
        label.htmlFor = input.id;
        label.append(document.createTextNode(size), input);
        return label;
      });
      row.replaceChildren(...labels);
      row.hidden = sizes.length === 1;
      const oneSize = item.querySelector('[data-bundle-one-size]');
      if (oneSize) oneSize.hidden = sizes.length !== 1;
      item.dataset.colourSizes = signature;
    }
    update() {
      this.syncExtras();
      const pack = this.optionValue(Number(this.dataset.packIndex), this.dataset.pack);
      const extras = this.optionValue(Number(this.dataset.extrasIndex), this.dataset.extras);
      const missing = [];
      for (const item of this.querySelectorAll('[data-bundle-item]')) {
        item.dataset.initialColour ||= item.dataset.colour;
        this.syncColour(item);
        const visible = item.dataset.addon !== 'true' || (Number(this.dataset.extrasIndex) >= 0 && (JSON.parse(item.dataset.extras || 'null') || []).includes(extras));
        this.showItem(item, visible);
        const inputs = [...item.querySelectorAll('[data-bundle-size]')];
        const property = item.querySelector('[data-bundle-property]');
        inputs.forEach(input => { input.disabled = !visible; });
        property.disabled = !visible;
        item.querySelectorAll('[data-bundle-colour-property], [data-bundle-colour-select], [data-bundle-colour]').forEach(input => { input.disabled = !visible; });
        const quantity = Number(JSON.parse(item.dataset.quantities)[pack]);
        const validQuantity = Number.isSafeInteger(quantity) && quantity >= 0;
        if (inputs.length === 1) inputs[0].value = visible && validQuantity ? quantity : 0;
        this.setText(item.querySelector('[data-bundle-heading]'), `${validQuantity ? quantity : '?'} × ${item.dataset.name}${item.dataset.colour ? ` – ${item.dataset.colour}` : ''}`);
        const values = inputs.map(input => Number(input.value));
        const total = values.reduce((sum, value) => sum + value, 0);
        const complete = validQuantity && inputs.length > 0 && values.every(value => Number.isSafeInteger(value) && value >= 0) && total === quantity;
        item.dataset.incomplete = String(!complete);
        if (complete || !visible) item.removeAttribute('data-error');
        const counter = item.querySelector('[data-bundle-total]');
        this.setText(counter, `${Number.isFinite(total) ? total : 0} of ${validQuantity ? quantity : '?'} chosen`);
        counter.dataset.complete = String(complete);
        property.value = inputs.filter((input, index) => values[index] > 0).map(input => `${input.dataset.bundleSize}: ${Number(input.value)}`).join(', ');
        if (visible && !complete) missing.push(`${validQuantity ? quantity : '?'} ${item.dataset.name}`);
      }
      this.valid = missing.length === 0 && this.querySelectorAll('[data-bundle-item]').length > 0;
      const controls = this.form.querySelector('[data-bundle-purchase-controls]');
      if (controls) { controls.disabled = false; controls.setAttribute('aria-disabled',String(!this.valid)); }
      this.form.querySelectorAll('[name="add"]').forEach(button => button.setAttribute('aria-disabled',String(!this.valid || button.disabled)));
      const payment = this.form.querySelector('.shopify-payment-button');
      if (payment) {
        // Accelerated checkout cannot upload the artwork: use the cart checkout when a file is selected.
        const blocked = !this.valid || Boolean(this.querySelector('input[type="file"]')?.files.length);
        payment.inert = blocked;
        payment.hidden = blocked;
      }
      const message = this.querySelector('[data-bundle-message]');
      this.setText(message, `Choose the sizes for every item${missing.length ? ` (${missing.join(', ')})` : ''}.`);
      message.hidden = this.valid;
    }
  }
  customElements.define('ft-workwear-bundle', WorkwearBundle);
})();
